import { IconType } from 'components/DesignSystem/Icon/icons';

/**
 * Conteudo (titulo + proposta de valor + cards) da tela "Solicitar acesso"
 * (`NoAccessPage`), dirigido por dados. Indexado pelo NOME DE EXIBICAO do
 * produto (`permissionProductMap`), pois varios permissionKeys mapeiam para o
 * mesmo produto (ex.: `prevs-automatico` -> "Flux"). Adicionar/editar um produto
 * = editar este mapa. Produto fora do mapa cai no fallback (sem cards).
 *
 * Os icones usam o conjunto existente do DesignSystem (aproximacoes do
 * significado pedido — facil de trocar aqui).
 */
export interface BenefitCard {
  icone: IconType;
  titulo: string;
  descricao: string;
}

export interface ProductAccessContent {
  iconeProduto?: IconType;
  titulo: string;
  subtitulo: string;
  cards: BenefitCard[];
}

const ACCESS_CONTENT: Record<string, ProductAccessContent> = {
  Windx: {
    iconeProduto: 'wind',
    titulo: 'Conheça o Windx',
    subtitulo:
      'Previsão de geração eólica com modelos meteorológicos combinados — para você '
      + 'antecipar a produção das suas usinas com mais confiança.',
    cards: [
      { icone: 'line_axis', titulo: 'Previsão de geração eólica', descricao: 'Geração e fator de capacidade por diversas granularidades espaciais.' },
      { icone: 'layers', titulo: 'Múltiplos modelos', descricao: 'Diversos modelos de previsão do tempo e reanálise para reduzir a incerteza.' },
      { icone: 'refresh', titulo: 'Atualização contínua', descricao: 'Dados ingeridos e processados automaticamente.' },
    ],
  },
  Flux: {
    iconeProduto: 'flux',
    titulo: 'Conheça o Flux',
    subtitulo:
      'Projeções de Energia Natural Afluente (ENA) com simulações de vazões a partir '
      + 'das chuvas previstas, para o setor elétrico.',
    cards: [
      {
        icone: 'flux',
        titulo: 'Projeções Automáticas de ENA',
        descricao:
          'ENA diária, semanal e mensal projetada com base em previsões meteorológicas, '
          + 'climatologia e outros datasets de precipitação de interesse para o setor elétrico.',
      },
      {
        icone: 'edit',
        titulo: 'Projeções Personalizadas de ENA',
        descricao:
          'Simulação com cenários que retratam a visão da sua equipe de meteorologia, seja a '
          + 'partir da costura de cenários dos grandes centros de referência em previsão '
          + 'meteorológica ou a partir do input direto dos volumes de chuva esperados em cada sub-bacia.',
      },
      {
        icone: 'stack',
        titulo: 'Cenários comparáveis',
        descricao:
          'Compare cenários hidrometeorológicos e visualize os impactos de atualizações na '
          + 'previsão, tendências e range de incertezas nas projeções.',
      },
      {
        icone: 'file',
        titulo: 'Arquivos a partir de vazões observadas',
        descricao:
          'Download de arquivos DESSEM e DECOMP prontos para uso em simulações que replicam '
          + 'exatamente a oferta hidroenergética do passado — ideal para estudos prospectivos, '
          + 'backtest da operação e análises de dupla-contabilização.',
      },
      {
        icone: 'download',
        titulo: 'Insumos prontos',
        descricao:
          'Cobertura de todas as necessidades usuais na montagem de estudos energéticos: '
          + 'mapas de precipitação, chuva média por bacia, arquivos DESSEM, PREVS.RVX e VAZOES.DAT.',
      },
      {
        icone: 'help',
        titulo: 'Documentação, tutoriais e equipe de apoio',
        descricao:
          'Para que sua equipe saiba sempre como executar estudos com diferentes graus de complexidade.',
      },
    ],
  },
  Meteorologia: {
    iconeProduto: 'meteorology',
    titulo: 'Conheça a solução de Meteorologia',
    subtitulo:
      'Previsões meteorológicas de curto, médio e longo prazo com acompanhamento '
      + 'técnico contínuo de meteorologistas.',
    cards: [
      { icone: 'meteorology', titulo: 'Relatórios e vídeos diários', descricao: 'Condições e previsões de chuva e temperatura nas bacias.' },
      { icone: 'user', titulo: 'Acompanhamento especializado', descricao: 'Reuniões semanais com meteorologista e boletins mensais de desempenho.' },
      { icone: 'chip', titulo: 'Modelo próprio PREVCONSENSO', descricao: 'IA + análise especializada, com desempenho superior aos modelos globais.' },
    ],
  },
  Agro: {
    iconeProduto: 'sprout',
    titulo: 'Conheça a solução para o Agro',
    subtitulo:
      'Previsões meteorológicas e climáticas com análises agrometeorológicas para o '
      + 'planejamento agrícola.',
    cards: [
      { icone: 'sprout', titulo: 'Balanço hídrico', descricao: 'Evapotranspiração e umidade do solo para apoiar o manejo.' },
      { icone: 'list', titulo: 'Indicadores agrometeorológicos', descricao: 'Acompanhamento do desenvolvimento de culturas.' },
      { icone: 'user', titulo: 'Reuniões técnicas semanais', descricao: 'Contextualização das previsões com meteorologistas.' },
    ],
  },
};

/** Conteudo do produto (display name) ou fallback generico (sem cards). */
export function getAccessContent(produto: string): ProductAccessContent {
  return ACCESS_CONTENT[produto] ?? {
    titulo: `Conheça ${produto}`,
    subtitulo:
      'Este produto não faz parte do seu plano atual. Fale com o nosso time comercial '
      + 'para conhecer ou contratar.',
    cards: [],
  };
}
